/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './GovernmentClose.module.scss';

type Translate = (key: string) => string;

const WAY_ICONS = {
  trial: (
    <>
      <path d="M9 3.5h6M10.5 3.5v5l-5 8.4A2 2 0 0 0 7.2 20h9.6a2 2 0 0 0 1.7-3.1l-5-8.4v-5" strokeLinejoin="round" />
      <path d="M8 15.5h8" strokeLinecap="round" />
    </>
  ),
  api: (
    <>
      <path d="M9 8 4.5 12 9 16" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15 8l4.5 4-4.5 4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13.5 5 10.5 19" strokeLinecap="round" />
    </>
  ),
  case: (
    <>
      <path d="M5 20V6.5A1.5 1.5 0 0 1 6.5 5h11A1.5 1.5 0 0 1 19 6.5V20" />
      <path d="M9 20v-6h6v6" />
      <path d="M9 10h.01M12 10h.01M15 10h.01" strokeLinecap="round" />
    </>
  ),
} as const;

type Way = {
  title: string;
  body: string;
  label: string;
  href: string;
  icon: keyof typeof WAY_ICONS;
  primary?: boolean;
};

const getWays = (t: Translate): Way[] => [
  {
    title: t('You want to try it'),
    body: t('Run checks against your own jurisdictions on real records, without an integration.'),
    label: t('Open a scoped evaluation'),
    href: '/free-trial',
    icon: 'trial',
  },
  {
    title: t('You are building against it'),
    body: t('Endpoints, response shapes, provenance fields and the full data dictionary.'),
    label: t('Read the API docs'),
    href: '/api',
    icon: 'api',
  },
  {
    title: t('You are building the business case'),
    body: t('The register behind every field, coverage per jurisdiction, licensing position and indicative bands.'),
    label: t('View data sources'),
    href: '/data-dictionary',
    icon: 'case',
    primary: true,
  },
];

export const GovernmentClose = () => {
  const { t } = useInt();
  const ref = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(ref, { threshold: 0.15 });
  const ways = getWays(t);

  return (
    <Section className={styles.root}>
      <div className={styles.head}>
        <TitleBlock
          title={t('Tell us which borders you need to see across.')}
          description={t('Three ways in, depending on whether you are testing it, building it or funding it.')}
        />
      </div>

      <div className={cn(styles.ways, revealed && styles.waysOn)} ref={ref}>
        {ways.map((way, i) => (
          <div
            className={cn(styles.way, way.primary && styles.wayPrimary)}
            style={{ '--d': `${i * 90}ms` } as CSSProperties}
            key={way.title}
          >
            <span className={styles.wayIcon}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {WAY_ICONS[way.icon]}
              </svg>
            </span>
            <h3 className={styles.wayTitle}>{way.title}</h3>
            <p className={styles.wayBody}>{way.body}</p>
            <Button type={way.primary ? 'blue' : 'dark-outline'} size="lg" href={way.href} suffixIcon="arrow-forward">
              {way.label}
            </Button>
          </div>
        ))}
      </div>
    </Section>
  );
};
